import type { Snapshot, Row } from "./api";
import { api, ApiError } from "./api";
const DB = "parlyst-counts-v1",
  TTL = 8 * 60 * 60 * 1000;
type Cache = { scope: string; savedAt: number; data: Snapshot };
export type Pending = {
  id: string;
  scope: string;
  countId: string;
  body: any;
  createdAt: number;
  error?: string;
  /** Counting flow: an earlier save of the same bottle that was being sent when this one was made (see flow/outbox.ts). */
  after?: string | null;
  /** Counting flow: "error" once the server refused it (kept for review, never resent on its own). */
  state?: "pending" | "error";
};
async function db() {
  return new Promise<IDBDatabase>((resolve, reject) => {
    const r = indexedDB.open(DB, 2);
    r.onupgradeneeded = () => {
      const d = r.result;
      if (!d.objectStoreNames.contains("cache"))
        d.createObjectStore("cache", { keyPath: "scope" });
      if (!d.objectStoreNames.contains("pending"))
        d.createObjectStore("pending", { keyPath: "id" });
      // Count details (observations) for the counts opened on this device.
      if (!d.objectStoreNames.contains("counts"))
        d.createObjectStore("counts", { keyPath: "key" });
    };
    r.onsuccess = () => resolve(r.result);
    r.onerror = () => reject(r.error);
  });
}
async function run<T>(
  store: string,
  mode: IDBTransactionMode,
  fn: (s: IDBObjectStore) => IDBRequest,
): Promise<T> {
  const d = await db();
  return new Promise((resolve, reject) => {
    const tx = d.transaction(store, mode);
    const r = fn(tx.objectStore(store));
    tx.oncomplete = () => {
      d.close();
      resolve(r.result);
    };
    tx.onerror = () => {
      d.close();
      reject(tx.error);
    };
    tx.onabort = () => {
      d.close();
      reject(tx.error);
    };
  });
}
/** Cached data is scoped by person, company and venue. */
export const scopeOf = (s: Snapshot) => `${s.user.id}:${s.workspace.id}:${s.venue?.id ?? ""}`;
export async function cacheWorkspace(s: Snapshot) {
  const scope = scopeOf(s);
  await run("cache", "readwrite", (x) =>
    x.put({ scope, savedAt: Date.now(), data: s }),
  );
  localStorage.setItem("parlyst-scope", scope);
}
export async function offlineWorkspace() {
  const scope = localStorage.getItem("parlyst-scope");
  if (!scope) return null;
  const c = await run<Cache | undefined>("cache", "readonly", (x) =>
    x.get(scope),
  );
  return c && Date.now() - c.savedAt < TTL ? c.data : null;
}
/** Caches one count's detail (observations) for offline counting. */
export async function cacheCount(s: Snapshot, count: Row) {
  await run("counts", "readwrite", (x) =>
    x.put({ key: `${scopeOf(s)}:${count.id}`, savedAt: Date.now(), data: count }),
  );
}
export async function offlineCount(s: Snapshot, countId: string) {
  const c = await run<{ savedAt: number; data: Row } | undefined>(
    "counts",
    "readonly",
    (x) => x.get(`${scopeOf(s)}:${countId}`),
  );
  return c && Date.now() - c.savedAt < TTL ? c.data : null;
}
export async function pending(scope: string) {
  return (
    await run<Pending[]>("pending", "readonly", (x) => x.getAll())
  ).filter((p) => p.scope === scope);
}
export async function enqueue(s: Snapshot, countId: string, body: any) {
  const scope = scopeOf(s);
  const list = await pending(scope);
  if (
    list.some(
      (p) =>
        p.countId === countId &&
        p.body.productId === body.productId &&
        p.body.areaId === body.areaId,
    )
  )
    throw new Error(
      "This product already has a pending entry. Sync or resolve it before replacing the entry.",
    );
  const p = {
    id: body.idempotencyKey,
    scope,
    countId,
    body,
    createdAt: Date.now(),
  };
  await run("pending", "readwrite", (x) => x.put(p));
  return p;
}
/** Writes one outbox row as it is (the counting flow's optimistic outbox manages replacement itself). */
export async function putPending(p: Pending) {
  await run("pending", "readwrite", (x) => x.put(p));
}
/** Caches the counting flow's prefetch bundle of a count (products, shelves, last counts) for offline use. */
export async function cacheBundle(s: Snapshot, countId: string, bundle: Row) {
  await run("counts", "readwrite", (x) => x.put({ key: `${scopeOf(s)}:${countId}:bundle`, savedAt: Date.now(), data: bundle }));
}
export async function offlineBundle(s: Snapshot, countId: string) {
  const c = await run<{ savedAt: number; data: Row } | undefined>("counts", "readonly", (x) => x.get(`${scopeOf(s)}:${countId}:bundle`));
  return c && Date.now() - c.savedAt < TTL ? c.data : null;
}
export async function syncPending(s: Snapshot) {
  const list = await pending(scopeOf(s));
  let done = 0;
  for (const p of list) {
    // Refused by the server once: kept for the person to review, not resent on its own.
    if (p.state === "error") continue;
    try {
      await api(`/counts/${p.countId}/observations`, p.body, "PUT");
      await run("pending", "readwrite", (x) => x.delete(p.id));
      done++;
    } catch (e) {
      if (e instanceof ApiError) {
        p.error = e.message;
        await run("pending", "readwrite", (x) => x.put(p));
        if (e.status === 401 || e.status === 403) break;
      } else break;
    }
  }
  return done;
}
export async function removePending(id: string) {
  await run("pending", "readwrite", (x) => x.delete(id));
}
/**
 * Clears this device's offline data. With keepUnsynced (signing out anyway
 * while entries wait), only the cached copy of the workspace goes; the waiting
 * entries and their counts stay so they can be sent after the next sign-in.
 */
export async function clearOffline(options: { keepUnsynced?: boolean } = {}) {
  await run("cache", "readwrite", (x) => x.clear());
  if (options.keepUnsynced) return;
  localStorage.removeItem("parlyst-scope");
  await run("pending", "readwrite", (x) => x.clear());
  await run("counts", "readwrite", (x) => x.clear());
}
